import {useSyncExternalStore} from 'react';
import {getSetting, setSetting} from './persistentSettings';

/**
 * posPreferences.js
 *
 * Tiny app-wide preference store for the phone-first POS pass: layout mode
 * (Auto / Classic), haptic feedback, "start on the POS Hub" and the always-dark
 * POS look. Kept outside
 * SystemContext on purpose — it is read synchronously from non-React code (the
 * haptics helper) and by many screens, and a module store + `useSyncExternalStore`
 * avoids re-rendering the whole SystemContext tree on a toggle.
 *
 * Values live in persistentSettings (AsyncStorage + SecureStore double write), so
 * they survive restarts and process kills. Reads are synchronous against an
 * in-memory snapshot that is hydrated once at startup; until hydration finishes
 * the documented defaults apply.
 */

/** @type {{posLayoutMode: 'auto'|'classic', hapticsEnabled: boolean, posStartOnHub: boolean, posTablesView: 'list'|'map', posDarkMode: boolean}} */
export const POS_PREFERENCE_DEFAULTS = Object.freeze({
    posLayoutMode: 'auto',
    hapticsEnabled: true,
    posStartOnHub: true,
    posTablesView: 'list',
    posDarkMode: true,
});

const STORAGE_PREFIX = 'posPref.';

/**
 * Coerces a raw stored value into a valid preference value.
 *
 * @param {string} key One of the POS_PREFERENCE_DEFAULTS keys.
 * @param {*} raw Raw stored (string) or incoming value.
 * @returns {*} A valid value; the default when `raw` is missing or garbage.
 */
export const normalizePosPreference = (key, raw) => {
    const fallback = POS_PREFERENCE_DEFAULTS[key];

    if (key === 'posLayoutMode') {
        return raw === 'classic' || raw === 'auto' ? raw : fallback;
    }

    if (key === 'posTablesView') {
        return raw === 'list' || raw === 'map' ? raw : fallback;
    }

    if (key === 'hapticsEnabled' || key === 'posStartOnHub' || key === 'posDarkMode') {
        if (raw === true || raw === '1' || raw === 'true') {
            return true;
        }
        if (raw === false || raw === '0' || raw === 'false') {
            return false;
        }

        return fallback;
    }

    return fallback;
};

/**
 * Serialises a preference value for storage (booleans become '1' / '0').
 *
 * @param {string} key Preference key.
 * @param {*} value Valid value.
 * @returns {string}
 */
const serialize = (key, value) => (typeof POS_PREFERENCE_DEFAULTS[key] === 'boolean' ? (value ? '1' : '0') : String(value));

let snapshot = {...POS_PREFERENCE_DEFAULTS};
let hydration = null;
const listeners = new Set();

const emit = () => {
    listeners.forEach((listener) => {
        try {
            listener();
        } catch (_error) {
            // A throwing subscriber must never break the others.
        }
    });
};

/**
 * Loads the persisted preferences once. Safe to call repeatedly (same promise).
 *
 * @returns {Promise<void>}
 */
export const hydratePosPreferences = () => {
    if (!hydration) {
        hydration = Promise.all(Object.keys(POS_PREFERENCE_DEFAULTS).map(async (key) => {
            const raw = await getSetting(`${STORAGE_PREFIX}${key}`, null);

            return [key, raw == null ? POS_PREFERENCE_DEFAULTS[key] : normalizePosPreference(key, raw)];
        })).then((entries) => {
            const next = {...snapshot};
            let changed = false;

            entries.forEach(([key, value]) => {
                if (next[key] !== value) {
                    next[key] = value;
                    changed = true;
                }
            });
            if (changed) {
                snapshot = next;
                emit();
            }
        }).catch(() => {
            // Storage unavailable: keep the defaults.
        });
    }

    return hydration;
};

/**
 * Synchronous read of one preference.
 *
 * @param {string} key Preference key.
 * @returns {*}
 */
export const getPosPreference = (key) => snapshot[key];

/**
 * Updates and persists one preference, notifying every subscriber at once.
 *
 * @param {string} key Preference key.
 * @param {*} value New value (normalised; garbage falls back to the default).
 * @returns {Promise<void>}
 */
export const setPosPreference = async (key, value) => {
    if (!(key in POS_PREFERENCE_DEFAULTS)) {
        return;
    }

    const next = normalizePosPreference(key, value);

    if (snapshot[key] !== next) {
        snapshot = {...snapshot, [key]: next};
        emit();
    }

    try {
        await setSetting(`${STORAGE_PREFIX}${key}`, serialize(key, next));
    } catch (_error) {
        // Non-fatal: the in-memory value still applies for this session.
    }
};

/**
 * Subscribes to preference changes.
 *
 * @param {function(): void} listener Called after any change.
 * @returns {function(): void} Unsubscribe.
 */
export const subscribePosPreferences = (listener) => {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
};

/**
 * Test helper: restores the defaults and forgets hydration.
 */
export const resetPosPreferencesForTests = () => {
    snapshot = {...POS_PREFERENCE_DEFAULTS};
    hydration = null;
    listeners.clear();
};

/**
 * React hook for one preference; re-renders when it changes.
 *
 * @param {string} key Preference key.
 * @returns {*}
 */
export const usePosPreference = (key) => useSyncExternalStore(
    subscribePosPreferences,
    () => snapshot[key],
    () => POS_PREFERENCE_DEFAULTS[key],
);
